/**
 * @lastAudited 2025-01-19
 * @auditStatus PASS
 * @auditNotes Pre-publish checklist passed. No issues found.
 */
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useVehicles } from '../../hooks/useVehicles';
import VehicleCard from '../inventory/components/VehicleCard';
import { useSEOHead } from '../../hooks/useSEOHead';
import Breadcrumb from '../../components/feature/Breadcrumb';
import { useEffect } from 'react';
import { injectJsonLd, removeJsonLd } from '../../lib/schemaBuilder';

export default function UsedCarsSeasidePage() {
  useSEOHead({
    title: 'Used Cars Seaside OR — Beach-Ready, No Tourist Pricing | Rockhound Auto',
    description: 'Quality used cars near Seaside, OR. Coastal-tested vehicles that handle Highway 101 curves and beach parking. Free delivery to Seaside.',
    keywords: 'used cars Seaside Oregon, Seaside used vehicles, cars near Seaside OR',
    canonicalUrl: 'https://rockhoundauto.com/used-cars-seaside-oregon',
    geoPosition: '45.9929;-123.9229',
    geoRegion: 'US-OR',
    geoPlacename: 'Seaside, Oregon',
  });

  const { vehicles, loading } = useVehicles();
  const [selectedType, setSelectedType] = useState<string>('all');

  const filteredVehicles = vehicles
    .filter(v => v.status === 'available')
    .filter(v => selectedType === 'all' || v.type === selectedType)
    .slice(0, 6);

  const faqs = [
    {
      question: "Why do Seaside residents buy cars from Rockhound Auto in Warrenton?",
      answer: "Seaside buyers choose Rockhound Auto because we offer transparent pricing without dealership pressure, free delivery to Seaside homes, and a fast buying process that respects your time. We're just 20 minutes away in Warrenton, but we bring vehicles to you for test drives."
    },
    {
      question: "What types of vehicles are most popular in Seaside Oregon?",
      answer: "Seaside residents prefer SUVs and crossovers with AWD for beach access and coastal weather. Honda Pilot, Toyota Highlander, Subaru Outback, and Ford Explorer are top choices. Families need space for beach gear, bikes, and surfboards."
    },
    {
      question: "Do you deliver used cars to Seaside for free?",
      answer: "Yes, we offer free delivery anywhere in Seaside, Oregon. We'll bring the vehicle to your home for a test drive. If you love it, we complete the paperwork on-site. If not, no pressure or obligation."
    },
    {
      question: "How long does it take to buy a car from Rockhound Auto?",
      answer: "Most Seaside customers complete the entire purchase in 30 to 60 minutes, including paperwork and financing. You can also take days to decide—we work on your timeline, not ours. No dealership pressure tactics."
    },
    {
      question: "What makes buying a car in Seaside different from traditional dealerships?",
      answer: "Traditional dealerships require driving to Portland or Longview, spending hours negotiating, and dealing with surprise fees. Rockhound Auto brings vehicles to Seaside, offers transparent pricing online, and completes deals fast without pressure or hidden costs."
    },
    {
      question: "Are your used cars inspected and ready for coastal weather?",
      answer: "Every vehicle is inspected for coastal durability, including rust protection, seals, and mechanical reliability. We understand salt air and moisture challenges. All vehicles are detailed and ready for Oregon Coast conditions before delivery."
    },
    {
      question: "Can I get financing for a used car in Seaside Oregon?",
      answer: "Yes, we work with local credit unions and lenders who understand coastal Oregon. We help Seaside residents secure competitive financing, including options for trade-ins and flexible terms. No finance office pressure or unnecessary add-ons."
    },
    {
      question: "What's the best vehicle for Seaside beach access and family trips?",
      answer: "For Seaside families, we recommend AWD SUVs like the Honda Pilot, Toyota Highlander, or Subaru Outback. These offer three-row seating, roof racks for beach gear, rust resistance, and reliable performance in coastal weather and sandy beach access roads."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  useEffect(() => {
    injectJsonLd('schema-seaside-faq', faqSchema);

    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://rockhoundauto.com/'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Used Cars',
          item: 'https://rockhoundauto.com/inventory'
        },
        {
          '@type': 'ListItem',
          position: 3,
          name: 'Seaside Oregon',
          item: 'https://rockhoundauto.com/used-cars-seaside-oregon'
        }
      ]
    };

    injectJsonLd('schema-seaside-breadcrumb', breadcrumbSchema);

    return () => {
      removeJsonLd('schema-seaside-faq');
      removeJsonLd('schema-seaside-breadcrumb');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Breadcrumb items={[{ label: 'Home', path: '/' }, { label: 'Used Cars Seaside Oregon' }]} />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://public.readdy.ai/ai/img_res/fae4fb0384a79541c0e69f27b5d75100.jpg')] bg-cover bg-center opacity-20"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Used Cars for Seaside That Can Handle the Turnaround Traffic
            </h1>
            <p className="text-xl sm:text-2xl text-slate-200 mb-8 leading-relaxed">
              We're 15 minutes north in Warrenton. Every car we sell is ready for summer tourist gridlock, winter storm commutes, and the occasional sand-in-everything situation. Because if it can't survive Seaside parking lots, it doesn't belong on our lot.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/inventory"
                className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-car-line text-xl"></i>
                See Beach-Ready Inventory
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all border border-white/20 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl"></i>
                Talk to a Local
              </Link>
            </div>
            
            <div className="mt-12 grid grid-cols-3 gap-6">
              <div>
                <div className="text-3xl font-bold text-teal-400 mb-1">15 min</div>
                <div className="text-sm text-slate-300">From the Turnaround</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-teal-400 mb-1">Free</div>
                <div className="text-sm text-slate-300">Delivery to Seaside</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-teal-400 mb-1">Zero</div>
                <div className="text-sm text-slate-300">Tourist-Trap Pricing</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Seaside Buyers Choose Us */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Built for Seaside Life
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              We know what your car goes through here — salt spray, sand, fog that won't quit, and parking lots that flood during king tides. Every vehicle is inspected with that reality in mind. Check out our full selection of <Link to="/inventory" className="text-teal-700 font-semibold underline underline-offset-2 hover:text-teal-900 transition-colors">used cars near Seaside</Link> ready for coastal conditions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-water-flash-line text-2xl text-teal-600"></i>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Coastal Durability Tested</h3>
                <p className="text-slate-600 leading-relaxed">
                  We check seals, undercarriage, and electrical systems for salt damage. If it's been sitting in beach parking lots without proper care, we pass. You need a car that'll survive more than one Seaside winter.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-map-pin-line text-2xl text-teal-600"></i>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Quick Drive from the Prom</h3>
                <p className="text-slate-600 leading-relaxed">
                  We're 15 minutes north on 101 in Warrenton. Swing by after hitting Pig 'N Pancake or before a Tillamook Head hike. We'll even meet you at the Cove for a beach test drive if you want.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-truck-line text-2xl text-teal-600"></i>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">We Deliver to Your Driveway</h3>
                <p className="text-slate-600 leading-relaxed">
                  Live in Seaside Heights or down by Gearhart? We'll bring the car to you. No need to coordinate rides or take time off work. We handle the logistics so you don't have to.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                  <i className="ri-user-heart-line text-2xl text-teal-600"></i>
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Locals Helping Locals</h3>
                <p className="text-slate-600 leading-relaxed">
                  We're not some out-of-town dealer dumping inventory on coast folks. We live here. We know the roads, the weather, and what actually holds up. If we sell you junk, we'll see you at the grocery store. Reputation matters.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why People Buy Here */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-slate-900">
              Why Seaside Residents Buy Cars Here
            </h2>
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 border-l-4 border-teal-500 p-8 rounded-r-xl mb-8">
              <p className="text-lg text-slate-800 leading-relaxed">
                Seaside is Oregon's classic beach town with 6,800 residents who value family time, outdoor recreation, and avoiding tourist crowds. Buying a car shouldn't mean driving to Portland or Longview and wasting a Saturday at a dealership. Rockhound Auto brings quality used vehicles to Seaside with free delivery, transparent pricing, and a buying process that takes less than an hour.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-home-smile-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Test Drive at Your Seaside Home</h3>
                <p className="text-slate-700">
                  We deliver vehicles to your Seaside driveway for test drives. Drive on your streets, check beach access, test parking downtown. If you love it, we complete paperwork on-site. If not, no pressure.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-price-tag-3-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Transparent Pricing Always</h3>
                <p className="text-slate-700">
                  The price online is the real price. No negotiation games, no "manager approval" needed, no surprise fees in the finance office. What you see is what you pay—simple and honest.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Coastal-Ready Vehicles</h3>
                <p className="text-slate-700">
                  Every vehicle is inspected for coastal durability—rust protection, seals, and mechanical reliability. We understand salt air and moisture challenges. All vehicles are detailed and ready for Oregon Coast conditions.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">Fast Process, Your Timeline</h3>
                <p className="text-slate-700">
                  Most Seaside customers complete the entire purchase in 30-60 minutes. Or take days to decide—we work on your timeline. No dealership pressure, no wasted weekends, no high-pressure sales tactics.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why They Drive to Rockhound */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-slate-900">
              Why Seaside Buyers Choose Rockhound Auto
            </h2>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-l-4 border-amber-500 p-8 rounded-r-xl mb-8">
              <p className="text-lg text-slate-800 leading-relaxed">
                Seaside families choose Rockhound Auto because we're not a traditional dealership. We're an auto broker in Warrenton, just 20 minutes north, offering free delivery to Seaside. No sales floor pressure, no finance office tricks, no wasting your Saturday when you could be at the beach with your family.
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-teal-500">
                <h3 className="text-xl font-bold text-slate-900 mb-3">We Bring Vehicles to You</h3>
                <p className="text-slate-700">
                  Browse inventory online from your Seaside home. Found something you like? We deliver it for a free test drive. Drive it on your streets, check beach access, test parking downtown. Love it? We complete paperwork on-site. Not right? No hard feelings.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-teal-500">
                <h3 className="text-xl font-bold text-slate-900 mb-3">No Dealership Games</h3>
                <p className="text-slate-700">
                  Traditional dealerships play the four-square game, send you to a "manager" for approval, and hit you with surprise fees in finance. We don't. The price you see online is the real price. No negotiation, no tricks, no hidden costs.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-teal-500">
                <h3 className="text-xl font-bold text-slate-900 mb-3">We Understand Coastal Living</h3>
                <p className="text-slate-700">
                  Seaside buyers need vehicles that handle salt air, moisture, and sandy beach access roads. We inspect for rust resistance, good seals, and reliable mechanics. We stock SUVs with AWD, trucks for hauling beach gear, and family vehicles built for coastal life.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-sm border-l-4 border-teal-500">
                <h3 className="text-xl font-bold text-slate-900 mb-3">Local Credit Union Financing</h3>
                <p className="text-slate-700">
                  We work with Oregon credit unions who understand coastal communities. Competitive rates, flexible terms, and no finance office pressure. We help with trade-ins and down payments. No extended warranties you don't need.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dealer Insight */}
      <section className="py-16 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-user-star-line text-3xl text-white"></i>
              </div>
              <div>
                <h2 className="text-3xl font-bold mb-2">Dealer Insight</h2>
                <p className="text-teal-400 font-semibold">Local Market Knowledge</p>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-8">
              <p className="text-xl leading-relaxed mb-6">
                "Seaside buyers want family-friendly SUVs with space for beach gear, bikes, and surfboards. Honda Pilot, Toyota Highlander, and Subaru Outback dominate because they offer three-row seating, roof racks, AWD for winter storms, and rust resistance for coastal weather. Families also value fuel efficiency for trips to Portland and reliable brands that won't nickel-and-dime them with repairs."
              </p>
              <p className="text-slate-300 italic">
                — Rockhound Auto, Warrenton OR
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-slate-900 text-center">
              Frequently Asked Questions About Buying Used Cars in Seaside Oregon
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {faq.question}
                  </h3>
                  <p className="text-slate-700 leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Available Inventory */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4 text-slate-900">
              Used Cars Available for Seaside Delivery
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              All vehicles are inspected, detailed, and ready for free delivery to Seaside, Oregon.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => setSelectedType('all')}
              className={`px-6 py-2 rounded-full font-semibold transition-colors whitespace-nowrap ${
                selectedType === 'all'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              All Vehicles
            </button>
            <button
              onClick={() => setSelectedType('SUV')}
              className={`px-6 py-2 rounded-full font-semibold transition-colors whitespace-nowrap ${
                selectedType === 'SUV'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              SUVs
            </button>
            <button
              onClick={() => setSelectedType('Truck')}
              className={`px-6 py-2 rounded-full font-semibold transition-colors whitespace-nowrap ${
                selectedType === 'Truck'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              Trucks
            </button>
            <button
              onClick={() => setSelectedType('Sedan')}
              className={`px-6 py-2 rounded-full font-semibold transition-colors whitespace-nowrap ${
                selectedType === 'Sedan'
                  ? 'bg-teal-600 text-white'
                  : 'bg-white text-slate-700 hover:bg-slate-100'
              }`}
            >
              Sedans
            </button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
            </div>
          ) : filteredVehicles.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {filteredVehicles.map((vehicle) => (
                  <VehicleCard key={vehicle.id} vehicle={vehicle} />
                ))}
              </div>
              <div className="text-center">
                <Link
                  to="/inventory"
                  className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors whitespace-nowrap"
                >
                  View All Available Inventory
                </Link>
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-white rounded-xl">
              <i className="ri-car-line text-6xl text-slate-300 mb-4"></i>
              <p className="text-xl text-slate-600 mb-6">
                No vehicles currently match your filter. Check back soon or view all inventory.
              </p>
              <Link
                to="/inventory"
                className="inline-block bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors whitespace-nowrap"
              >
                View All Inventory
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-teal-600 to-cyan-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Buy a Car in Seaside Oregon?
          </h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-90">
            Browse our inventory online, schedule a free test drive in Seaside, and complete the purchase in under an hour. No dealership pressure, no hidden fees, no wasted weekends.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/inventory"
              className="bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-slate-100 transition-colors whitespace-nowrap"
            >
              View Available Inventory
            </Link>
            <Link
              to="/contact"
              className="bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-teal-800 transition-colors whitespace-nowrap border-2 border-white/30"
            >
              Schedule Test Drive
            </Link>
          </div>
        </div>
      </section>

      {/* Cross-link to Service Area page */}
      <section className="py-12 bg-slate-50 border-t border-slate-200">
        <div className="container mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 bg-white rounded-xl p-6 shadow-sm border border-slate-200 max-w-4xl mx-auto">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-map-2-line text-2xl text-teal-600"></i>
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">
                  Rockhound Auto Serves Seaside, Oregon
                </h2>
                <p className="text-sm text-slate-600 mt-0.5">
                  See full service details, nearby cities, and delivery coverage for the Seaside area.
                </p>
              </div>
            </div>
            <Link
              to="/service-areas/seaside"
              className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              <i className="ri-map-pin-line"></i>
              Seaside Service Area Details
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}